import { useSyncExternalStore } from 'react'

/**
 * Day / night, in one place.
 *
 * There are three modes, not two. `auto` is the default and follows the clock —
 * 5 PM AEST (UTC+10) is 7 AM UTC. `day` and `night` are a person saying "no,
 * this one", and that has to outrank the clock permanently, not until the next
 * tick.
 *
 * The distinction used to be lost because the resolved value and the stated
 * preference shared a localStorage key: the app wrote the resolved value on
 * every mount, so by the first tick there was always a "preference" and auto
 * could never fire. Only `setMode` writes here, and it only ever writes one of
 * the three modes.
 *
 * A module store rather than context because two unrelated places need it —
 * the topbar toggle and the appearance control on the profile screen — and
 * threading a provider through for one boolean is more plumbing than the
 * problem deserves.
 */
const KEY = 'beseter.theme'
const MODES = ['auto', 'day', 'night']

const listeners = new Set()
let mode = read()

function read() {
  const stored = localStorage.getItem(KEY)
  if (MODES.includes(stored)) return stored
  // One-time carry-over from the boolean this replaced, so nobody who had
  // already chosen night gets bounced back to the clock.
  const legacy = localStorage.getItem('beseter.night.override')
  if (legacy === '1') return 'night'
  if (legacy === '0') return 'day'
  return 'auto'
}

/** 5 PM AEST. Deliberately not the device's local time: the app's audience is
 *  Australian, and a writer on tour shouldn't see a different app. */
export function isNightHour() {
  return new Date().getUTCHours() >= 7
}

export function resolve(m = mode) {
  return m === 'auto' ? isNightHour() : m === 'night'
}

export function getMode() {
  return mode
}

export function setMode(next) {
  if (!MODES.includes(next) || next === mode) return
  mode = next
  localStorage.setItem(KEY, next)
  apply()
  listeners.forEach((fn) => fn())
}

function apply() {
  document.body.classList.toggle('night', resolve())
}

/** Auto has to keep up with the clock on a session left open overnight.
 *  One interval for the whole app, started once. */
let ticking = false
function startClock() {
  if (ticking) return
  ticking = true
  setInterval(() => {
    if (mode !== 'auto') return
    const was = document.body.classList.contains('night')
    if (was !== resolve()) {
      apply()
      listeners.forEach((fn) => fn())
    }
  }, 60000)
}

function subscribe(fn) {
  listeners.add(fn)
  startClock()
  return () => listeners.delete(fn)
}

apply()

/** Returns `[mode, setMode, isNight]`. */
export function useTheme() {
  const current = useSyncExternalStore(subscribe, getMode, getMode)
  return [current, setMode, resolve(current)]
}
